const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export const pickRecorderMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return MIME_CANDIDATES.find((mime) => MediaRecorder.isTypeSupported(mime)) || '';
};

export interface CallRecorder {
  addRemote: (stream: MediaStream) => void;
  stop: () => Promise<Blob | null>;
}

export const createCallRecorder = (local: MediaStream): CallRecorder | null => {
  if (typeof MediaRecorder === 'undefined' || typeof AudioContext === 'undefined') return null;
  const ctx = new AudioContext();
  const dest = ctx.createMediaStreamDestination();
  try {
    ctx.createMediaStreamSource(local).connect(dest);
  } catch {
    void ctx.close();
    return null;
  }
  void ctx.resume().catch(() => {});

  const mime = pickRecorderMime();
  let rec: MediaRecorder;
  try {
    rec = new MediaRecorder(dest.stream, mime ? { mimeType: mime } : undefined);
  } catch {
    void ctx.close();
    return null;
  }

  const chunks: BlobPart[] = [];
  rec.ondataavailable = (event) => {
    if (event.data?.size) chunks.push(event.data);
  };
  rec.start(1000);
  let remoteAdded = false;
  let stopped = false;

  return {
    addRemote(stream) {
      if (stopped || remoteAdded || !stream.getAudioTracks().length) return;
      try {
        ctx.createMediaStreamSource(stream).connect(dest);
        remoteAdded = true;
      } catch {
        /* already closed */
      }
    },
    stop() {
      if (stopped) return Promise.resolve(null);
      stopped = true;
      return new Promise((resolve) => {
        const finish = () => {
          void ctx.close().catch(() => {});
          const type = rec.mimeType || mime || 'audio/webm';
          resolve(chunks.length ? new Blob(chunks, { type }) : null);
        };
        rec.onstop = finish;
        if (rec.state === 'inactive') {
          finish();
          return;
        }
        try {
          rec.stop();
        } catch {
          finish();
        }
      });
    }
  };
};
